import React, { useState } from 'react';
import { ArrowLeft, Scissors, Star, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { salonData } from '../../mockDemos';
import { useNavigate } from 'react-router-dom';

export const SalonDemo = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('All');
  
  const categories = ['All', 'Hair', 'Skin', 'Nails', 'Makeup'];
  const filteredServices = selectedCategory === 'All' 
    ? salonData.services 
    : salonData.services.filter(s => s.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <Scissors className="text-orange-500" size={32} />
              <span className="text-2xl font-bold text-gray-900">{salonData.name}</span>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="border-orange-500 text-orange-500 hover:bg-orange-50"
            >
              <ArrowLeft className="mr-2" size={16} />
              Back to Portfolio
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-pink-50 via-orange-50 to-white">
        <div className="max-w-7xl mx-auto text-center space-y-6">
          <Badge className="bg-orange-500 text-white hover:bg-orange-600">
            Demo Website by Pranshik
          </Badge>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900">
            {salonData.hero.headline}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {salonData.hero.subtext}
          </p>
          <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white">
            Book Appointment
          </Button>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-4xl font-bold text-gray-900">Our Services</h2>
            <p className="text-xl text-gray-600">Pamper yourself with our premium treatments</p>
          </div>
          
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                className={selectedCategory === category ? 'bg-orange-500 hover:bg-orange-600' : 'border-orange-500 text-orange-500 hover:bg-orange-50'}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </Button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredServices.map((service) => (
              <Card key={service.id} className="border-2 hover:border-orange-500 transition-all duration-300 hover:shadow-lg">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-xl">{service.name}</CardTitle>
                    <Badge variant="outline" className="text-orange-600 border-orange-200">
                      {service.category}
                    </Badge>
                  </div>
                  <CardDescription className="mt-2">
                    <div className="flex items-center space-x-2 text-sm">
                      <Clock size={16} />
                      <span>{service.duration}</span>
                    </div>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between items-center">
                    <span className="text-2xl font-bold text-orange-500">{service.price}</span>
                    <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white">
                      Book Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Staff Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-4xl font-bold text-gray-900">Meet Our Experts</h2>
            <p className="text-xl text-gray-600">Skilled professionals dedicated to your beauty</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {salonData.staff.map((member) => (
              <Card key={member.id} className="text-center border-2 hover:border-orange-500 transition-all duration-300">
                <CardHeader>
                  <Avatar className="w-32 h-32 mx-auto mb-4">
                    <AvatarImage src={member.avatar} alt={member.name} />
                    <AvatarFallback>{member.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <CardTitle className="text-2xl">{member.name}</CardTitle>
                  <CardDescription className="text-base">{member.role}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-center space-x-2 text-orange-500">
                    <Star size={20} className="fill-orange-500" />
                    <span className="font-semibold">{member.experience} Experience</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-4xl font-bold text-gray-900">Our Work</h2>
            <p className="text-xl text-gray-600">See the transformations we create</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {salonData.gallery.map((image, idx) => (
              <div key={idx} className="relative overflow-hidden rounded-lg aspect-square group">
                <img
                  src={image}
                  alt={`Gallery ${idx + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto text-center space-y-4">
          <div className="flex items-center justify-center space-x-3">
            <Scissors size={28} />
            <span className="text-2xl font-bold">{salonData.name}</span>
          </div>
          <p className="text-gray-400">{salonData.tagline}</p>
          <div className="pt-4 border-t border-gray-800">
            <p className="text-sm text-gray-500">
              This is a demo website created by <span className="text-orange-500 font-semibold">Pranshik</span>
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Want a website like this? <a href="/" className="text-orange-500 hover:underline">Contact me</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
